import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../auth/AuthContext';
import ModuleShell from '../../components/ModuleShell';
import {
  ADMIN_LOG_USER_VIEW,
  ADMIN_REMOVE_USER_TIER_OVERRIDE,
  ADMIN_SET_USER_TIER_OVERRIDE,
  ADMIN_USER_EFFECTIVE_LIMITS,
  ADMIN_USERS,
  ADMIN_USER_SEARCH,
  ADMIN_USER_SEARCH_CORE,
  ADMIN_USER_TIER_OVERRIDES,
} from '../../graphql/adminQueries';
import { formatDate, formatDateTime } from '../../utils/format';
import { formatAcquisitionDisplay, readAcquisitionRecord } from '../../utils/acquisitionPlatform';
import { displaySignupSource } from '../../utils/signupSource';

const DIRECTORY_PAGE_SIZE = 10;

const LIMIT_TYPES = ['accounts', 'entities', 'teamMembers'];

const UNKNOWN_FIELD = 'unknown';

/**
 * GraphQL schema predating website-api !410 rejects `signupAcquisition`.
 * Fail closed: treat as missing, do not invent a channel.
 */
export function isUnknownSignupAcquisitionFieldError(error) {
  const messages = [];
  if (error?.message) messages.push(String(error.message));
  for (const graphQLError of error?.graphQLErrors || []) {
    if (graphQLError?.message) messages.push(String(graphQLError.message));
  }
  const networkErrors = error?.networkError?.result?.errors;
  if (Array.isArray(networkErrors)) {
    for (const networkError of networkErrors) {
      if (networkError?.message) messages.push(String(networkError.message));
    }
  }
  // Also covers an API before migration 186 (no SignupAcquisition.cam / signupSource).
  return messages.some((message) =>
    /Cannot query field ["']?(signupAcquisition|cam|signupSource)["']?/i.test(message)
  );
}

export function acquisitionDisplayRows(user) {
  const record = readAcquisitionRecord(user);
  const display = formatAcquisitionDisplay(user);
  return [
    { key: 'platform', label: 'Platform', value: display.platform },
    {
      key: 'signupSource',
      label: 'Source',
      value: display.signupSource === UNKNOWN_FIELD
        ? UNKNOWN_FIELD
        : displaySignupSource(display.signupSource),
    },
    { key: 'referrer', label: 'Referrer', value: record.referrer || UNKNOWN_FIELD },
    { key: 'landingPage', label: 'Landing page', value: display.landingPage },
    { key: 'utmSource', label: 'utm_source', value: display.utmSource },
    { key: 'utmMedium', label: 'utm_medium', value: display.utmMedium },
    { key: 'utmCampaign', label: 'utm_campaign', value: display.utmCampaign },
    { key: 'utmContent', label: 'utm_content', value: display.utmContent },
    { key: 'utmTerm', label: 'utm_term', value: display.utmTerm },
    { key: 'gclid', label: 'gclid', value: display.gclid },
    { key: 'fbclid', label: 'fbclid', value: display.fbclid },
    { key: 'cam', label: 'cam (Hibu)', value: display.cam },
  ];
}

function PaginationRow({ offset, pageSize, total, loading, onOffset }) {
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + pageSize, total);
  return (
    <div className="pagination-row">
      <p className="pagination-summary">
        Showing {pageStart}–{pageEnd} of {total}
      </p>
      <div className="pagination-actions">
        <button
          type="button"
          className="secondary-button"
          disabled={offset <= 0 || loading}
          onClick={() => onOffset(Math.max(offset - pageSize, 0))}
        >
          Previous
        </button>
        <button
          type="button"
          className="secondary-button"
          disabled={offset + pageSize >= total || loading}
          onClick={() => onOffset(offset + pageSize)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function UserDirectoryTable({
  title,
  hint,
  sort,
  createdOn,
  dateKey,
  dateLabel,
  selectedId,
  onSelect,
}) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setOffset(0);
  }, [createdOn, sort]);

  const { data, loading, error } = useQuery(ADMIN_USERS, {
    variables: {
      sort,
      createdOn: createdOn || null,
      limit: DIRECTORY_PAGE_SIZE,
      offset,
    },
    fetchPolicy: 'network-only',
  });

  const page = data?.adminUsers;
  const users = page?.users || [];
  const total = page?.total ?? 0;

  return (
    <section className="chart-card">
      <h2>{title}</h2>
      {hint ? <p className="chart-hint">{hint}</p> : null}
      {error ? (
        <p className="chart-hint">Could not load users. Confirm API adminUsers is deployed.</p>
      ) : loading && users.length === 0 ? (
        <p className="chart-hint">Loading users…</p>
      ) : users.length === 0 ? (
        <p className="chart-empty">No users in this list.</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="data-table users-directory-table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Tier</th>
                  <th scope="col">{dateLabel}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={user.id === selectedId ? 'users-directory-row-selected' : undefined}
                  >
                    <td>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => onSelect(user)}
                      >
                        {user.email}
                      </button>
                    </td>
                    <td>{user.tierSlug || '—'}</td>
                    <td>{formatDateTime(user[dateKey])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationRow
            offset={offset}
            pageSize={DIRECTORY_PAGE_SIZE}
            total={total}
            loading={loading}
            onOffset={setOffset}
          />
        </>
      )}
    </section>
  );
}

function SignupAcquisitionSection({ user }) {
  const rows = acquisitionDisplayRows(user);
  return (
    <section className="chart-card" aria-labelledby="signup-acquisition-heading">
      <h2 id="signup-acquisition-heading">Signup acquisition</h2>
      <p className="chart-hint">
        First-touch. Platform uses Hibu cam / utm_source=hibu, then click ids, then utm_source,
        then the referrer host. No signal is unknown. Older users have no cam.
      </p>
      <div className="table-scroll">
        <table className="data-table users-acquisition-table">
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function UsersModule() {
  const { isAdmin, isStaff } = useAuth();
  const canMutate = isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const signedUpOnRaw = searchParams.get('signedUpOn') || '';
  const signedUpOn = /^\d{4}-\d{2}-\d{2}$/.test(signedUpOnRaw) ? signedUpOnRaw : '';
  const userIdParam = searchParams.get('userId') || '';

  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    limitType: 'accounts',
    overrideValue: '',
    effectiveTierSlug: '',
    reason: '',
    expiresAt: '',
  });

  const [searchUser, { loading: searchLoading }] = useLazyQuery(ADMIN_USER_SEARCH, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const [searchUserCore, { loading: coreSearchLoading }] = useLazyQuery(ADMIN_USER_SEARCH_CORE, {
    fetchPolicy: 'network-only',
  });
  const searchBusy = searchLoading || coreSearchLoading;

  const userId = selectedUser?.id;

  const {
    data: overridesData,
    loading: overridesLoading,
    refetch: refetchOverrides,
  } = useQuery(ADMIN_USER_TIER_OVERRIDES, {
    skip: !userId,
    variables: { userId },
    fetchPolicy: 'network-only',
  });

  const {
    data: limitsData,
    loading: limitsLoading,
    refetch: refetchLimits,
  } = useQuery(ADMIN_USER_EFFECTIVE_LIMITS, {
    skip: !userId,
    variables: { userId },
    fetchPolicy: 'network-only',
  });

  const [setOverride, { loading: setLoading }] = useMutation(ADMIN_SET_USER_TIER_OVERRIDE);
  const [removeOverride, { loading: removeLoading }] = useMutation(ADMIN_REMOVE_USER_TIER_OVERRIDE);
  const [logUserView] = useMutation(ADMIN_LOG_USER_VIEW);
  const loggedUserIdRef = useRef(null);

  useEffect(() => {
    if (!userId || loggedUserIdRef.current === userId) return;
    loggedUserIdRef.current = userId;
    logUserView({ variables: { userId } }).catch(() => {
      if (loggedUserIdRef.current === userId) loggedUserIdRef.current = null;
    });
  }, [userId, logUserView]);

  useEffect(() => {
    if (!userIdParam || selectedUser?.id === userIdParam) return undefined;
    let cancelled = false;
    searchUser({ variables: { query: userIdParam } })
      .then((result) => {
        if (cancelled) return;
        const user = result?.data?.adminUserSearch ?? null;
        if (user) setSelectedUser(user);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userIdParam, searchUser, selectedUser?.id]);

  const selectUser = (user) => {
    setSelectedUser(user);
    setSearchError(null);
    const next = new URLSearchParams(searchParams);
    next.set('userId', user.id);
    setSearchParams(next, { replace: true });
  };

  const clearSignedUpOn = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('signedUpOn');
    setSearchParams(next);
  };

  const overrides = overridesData?.adminUserTierOverrides || [];
  const limits = limitsData?.adminUserEffectiveLimits;
  const mutationBusy = setLoading || removeLoading;

  const handleSearch = async (event) => {
    event.preventDefault();
    const query = searchInput.trim();
    if (!query) {
      setSearchError('Enter an email address or user ID.');
      return;
    }
    setSearchError(null);
    try {
      const primary = await searchUser({ variables: { query } });
      let user = primary?.data?.adminUserSearch ?? null;
      if (!user && isUnknownSignupAcquisitionFieldError(primary?.error)) {
        const core = await searchUserCore({ variables: { query } });
        user = core?.data?.adminUserSearch ?? null;
      }
      if (user) {
        selectUser(user);
        return;
      }
      setSelectedUser(null);
      setSearchError(
        primary?.error && !isUnknownSignupAcquisitionFieldError(primary.error)
          ? primary.error.message
          : 'No user found for that email or ID.'
      );
    } catch (error) {
      if (isUnknownSignupAcquisitionFieldError(error)) {
        try {
          const core = await searchUserCore({ variables: { query } });
          const user = core?.data?.adminUserSearch ?? null;
          if (user) {
            selectUser(user);
            return;
          }
        } catch (coreError) {
          setSelectedUser(null);
          setSearchError(coreError.message || 'Search failed.');
          return;
        }
        setSelectedUser(null);
        setSearchError('No user found for that email or ID.');
        return;
      }
      setSelectedUser(null);
      setSearchError(error.message || 'Search failed.');
    }
  };

  const handleSetOverride = async () => {
    if (!canMutate || !userId) return;
    const { limitType, overrideValue, effectiveTierSlug, reason, expiresAt } = overrideForm;
    if (!limitType) {
      setSearchError('Select a limit type.');
      return;
    }
    if (overrideValue === '' && !effectiveTierSlug) {
      setSearchError('Provide an override value or effective tier slug.');
      return;
    }
    if (
      !window.confirm(
        `Apply ${limitType} override for ${selectedUser?.email}? This action is recorded in the admin audit log.`
      )
    ) {
      return;
    }
    try {
      await setOverride({
        variables: {
          userId,
          limitType,
          overrideValue: overrideValue === '' ? null : Number(overrideValue),
          effectiveTierSlug: effectiveTierSlug || null,
          reason: reason || null,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        },
      });
      setOverrideForm({
        limitType: 'accounts',
        overrideValue: '',
        effectiveTierSlug: '',
        reason: '',
        expiresAt: '',
      });
      await Promise.all([refetchOverrides(), refetchLimits()]);
    } catch (error) {
      setSearchError(error.message || 'Override action failed.');
    }
  };

  const handleRemoveOverride = async (limitType) => {
    if (!canMutate || !userId) return;
    if (
      !window.confirm(
        `Remove ${limitType} override for ${selectedUser?.email}? This action is recorded in the admin audit log.`
      )
    ) {
      return;
    }
    try {
      await removeOverride({ variables: { userId, limitType } });
      await Promise.all([refetchOverrides(), refetchLimits()]);
    } catch (error) {
      setSearchError(error.message || 'Override action failed.');
    }
  };

  const summaryLoading = overridesLoading || limitsLoading;

  return (
    <ModuleShell
      title="Users & support"
      description="Search by email or user ID, review recent signups and activity, and manage overrides."
    >
      <form className="users-search-form" onSubmit={handleSearch}>
        <label htmlFor="user-search-input">Search email or user ID</label>
        <div className="users-search-row">
          <input
            id="user-search-input"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="user@example.com or UUID"
            autoComplete="off"
          />
          <button type="submit" className="primary-button" disabled={searchBusy}>
            {searchBusy ? 'Searching…' : 'Search'}
          </button>
        </div>
      </form>

      {searchError && <p className="module-status module-status-error">{searchError}</p>}

      <div className="users-directory-grid">
        <UserDirectoryTable
          title="Recent signups"
          hint={
            signedUpOn
              ? `Customers who signed up on ${formatDate(signedUpOn)} (UTC).`
              : 'Newest customer accounts first. Excludes test and internal.'
          }
          sort="RECENT_SIGNUP"
          createdOn={signedUpOn || null}
          dateKey="createdAt"
          dateLabel="Signed up"
          selectedId={selectedUser?.id}
          onSelect={selectUser}
        />
        <UserDirectoryTable
          title="Recently active"
          hint="Customers with a last login, newest first. Excludes test and internal."
          sort="RECENT_ACTIVE"
          dateKey="lastLogin"
          dateLabel="Last login"
          selectedId={selectedUser?.id}
          onSelect={selectUser}
        />
      </div>
      {signedUpOn ? (
        <p className="module-footnote">
          <button type="button" className="text-button" onClick={clearSignedUpOn}>
            Clear signup-day filter
          </button>
        </p>
      ) : null}

      {selectedUser && (
        <>
          <div className="kpi-grid">
            <article className="kpi-card">
              <p className="kpi-label">User</p>
              <p className="kpi-value users-email-value">{selectedUser.email}</p>
              <p className="kpi-hint">ID: {selectedUser.id}</p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">Membership tier</p>
              <p className="kpi-value">{selectedUser.tierSlug || '—'}</p>
              <p className="kpi-hint">{selectedUser.tierName || 'No tier name'}</p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">Effective tier</p>
              <p className="kpi-value">{limits?.effectiveTier || '—'}</p>
              <p className="kpi-hint">
                {summaryLoading
                  ? 'Loading limits…'
                  : `Accounts ${limits?.accounts ?? '—'} · Entities ${limits?.entities ?? '—'} · Team ${limits?.teamMembers ?? '—'}`}
              </p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">Account status</p>
              <p className="kpi-value">{selectedUser.isActive ? 'Active' : 'Inactive'}</p>
              <p className="kpi-hint">
                Role: {selectedUser.role}
                {selectedUser.lastLogin ? ` · Last login ${formatDateTime(selectedUser.lastLogin)}` : ''}
              </p>
            </article>
          </div>

          <SignupAcquisitionSection user={selectedUser} />

          <section className="chart-card">
            <h2>Tier overrides</h2>
            {summaryLoading ? (
              <p className="module-status">Loading overrides…</p>
            ) : overrides.length === 0 ? (
              <p className="chart-empty">No active overrides for this user.</p>
            ) : (
              <div className="table-scroll">
                <table className="data-table users-overrides-table">
                  <thead>
                    <tr>
                      <th scope="col">Limit</th>
                      <th scope="col">Value</th>
                      <th scope="col">Tier slug</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Expires</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((row) => (
                      <tr key={row.id}>
                        <td>{row.limitType}</td>
                        <td>{row.overrideValue ?? '—'}</td>
                        <td>{row.effectiveTierSlug ?? '—'}</td>
                        <td>{row.reason ?? '—'}</td>
                        <td>{formatDateTime(row.expiresAt)}</td>
                        <td>
                          {canMutate ? (
                            <button
                              type="button"
                              className="text-button"
                              onClick={() => handleRemoveOverride(row.limitType)}
                              disabled={mutationBusy}
                            >
                              Remove
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="module-footnote">
              <Link to={`/operations?targetId=${encodeURIComponent(userId)}`}>
                View override audit history in Operations
              </Link>
            </p>
          </section>

          {canMutate ? (
            <section className="chart-card">
              <h2>Set tier override</h2>
              <form
                className="users-override-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSetOverride();
                }}
              >
                <label htmlFor="override-limit-type">Limit type</label>
                <select
                  id="override-limit-type"
                  value={overrideForm.limitType}
                  onChange={(event) =>
                    setOverrideForm((prev) => ({ ...prev, limitType: event.target.value }))
                  }
                >
                  {LIMIT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>

                <label htmlFor="override-value">Override value</label>
                <input
                  id="override-value"
                  type="number"
                  min="0"
                  value={overrideForm.overrideValue}
                  onChange={(event) =>
                    setOverrideForm((prev) => ({ ...prev, overrideValue: event.target.value }))
                  }
                  placeholder="Optional numeric limit"
                />

                <label htmlFor="override-tier-slug">Effective tier slug</label>
                <input
                  id="override-tier-slug"
                  type="text"
                  value={overrideForm.effectiveTierSlug}
                  onChange={(event) =>
                    setOverrideForm((prev) => ({ ...prev, effectiveTierSlug: event.target.value }))
                  }
                  placeholder="Optional tier slug (e.g. PRO)"
                />

                <label htmlFor="override-reason">Reason</label>
                <input
                  id="override-reason"
                  type="text"
                  value={overrideForm.reason}
                  onChange={(event) =>
                    setOverrideForm((prev) => ({ ...prev, reason: event.target.value }))
                  }
                  placeholder="Support ticket or context"
                />

                <label htmlFor="override-expires">Expires</label>
                <input
                  id="override-expires"
                  type="datetime-local"
                  value={overrideForm.expiresAt}
                  onChange={(event) =>
                    setOverrideForm((prev) => ({ ...prev, expiresAt: event.target.value }))
                  }
                />

                <button type="submit" className="primary-button" disabled={mutationBusy}>
                  Review override
                </button>
              </form>
              <p className="module-footnote">
                Overrides are audited. Confirm before saving; view entries under Operations.
              </p>
            </section>
          ) : (
            isStaff && (
              <p className="module-footnote">
                Read-only (STAFF): tier override controls are hidden. Contact an ADMIN for changes.
              </p>
            )
          )}
        </>
      )}
    </ModuleShell>
  );
}
